import { useApp } from '../../../../../../../hooks/AppProvider.tsx';
import { useSubEvent } from '../../../../../../../hooks/SubEventProvider.tsx';
import { useParticipantDetails } from '../../../hooks/ParticipantDetailsProvider.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import buildEventSubstitutions from './utils/buildEventSubstitutions.ts';
import buildSubEventSubstitutions from './utils/buildSubEventSubstitutions.ts';
import buildParticipantSubstitutions from './utils/buildParticipantSubstitutions.ts';
import buildAwardsSubstitutions from './utils/buildAwardsSubstitutions.ts';
import buildResultFieldSubstitutions from './utils/buildResultFieldSubstitutions.ts';
import buildCustomColumnSubstitutions from './utils/buildCustomColumnSubstitutions.ts';
import buildSegmentSubstitutions from './utils/buildSegmentSubstitutions.ts';
import buildSponsorLogoSubstitutions from './utils/buildSponsorLogoSubstitutions.ts';
import applySubstitutions from './utils/applySubstitutions.ts';
import type { SubstitutionMap } from './utils/types.ts';

type UseMergeTagSubstitutions = {
  content: string;
};

const useMergeTagSubstitutions = ({ content }: UseMergeTagSubstitutions): string => {
  const [{ event }] = useApp();
  const { subEvent } = useSubEvent();
  const [{ resultDetail }] = useParticipantDetails();
  const {
    i18n: { language: locale },
  } = useTranslation('translation');

  const substitutions = useMemo<SubstitutionMap | null>(() => {
    if (!event || !subEvent || !resultDetail) {
      return null;
    }

    const { participant, result, awards, finisherCertificateTemplate } = resultDetail;

    return {
      ...buildEventSubstitutions({ event, locale }),
      ...buildSubEventSubstitutions({ subEvent }),
      ...buildParticipantSubstitutions({ participant }),
      ...buildAwardsSubstitutions({ awards, content }),
      ...buildSponsorLogoSubstitutions({ template: finisherCertificateTemplate, content }),
      ...(result && {
        ...buildResultFieldSubstitutions({ result }),
        ...buildCustomColumnSubstitutions({ result, content }),
        ...buildSegmentSubstitutions({ result, subEvent, content }),
      }),
    };
  }, [event, subEvent, resultDetail, locale, content]);

  if (!substitutions) {
    return content;
  }

  return applySubstitutions({ content, substitutions });
};

export default useMergeTagSubstitutions;
